import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Camera, Loader2, Trash2, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const DEFAULT_ACCEPT = 'image/png,image/jpeg,image/webp';
const DEFAULT_MAX_BYTES = 5 * 1024 * 1024; // 5 MB

export interface ImageUploaderProps {
  /**
   * Current preview node — usually a `<BusinessAvatar>` or
   * `<ClientAvatar>`. Falls through whatever placeholder the avatar
   * component already renders when `imageUrl` is null.
   */
  preview: ReactNode;
  /** Called once the user picks a file we accept. */
  onUpload: (file: File) => Promise<void> | void;
  /**
   * Optional remove handler. When omitted the "Remove" button is
   * hidden (some surfaces — like client photo upload — don't have a
   * dedicated delete endpoint yet).
   */
  onRemove?: () => Promise<void> | void;
  /** Disables the input + buttons while a mutation is in flight. */
  loading?: boolean;
  /** When true, the "Remove" button is shown. */
  hasImage?: boolean;
  /** Override the title shown above the buttons. */
  title?: string;
  /** Override the help line under the title. */
  hint?: string;
  /** MIME types the file picker accepts. */
  accept?: string;
  /** Max bytes; files above this size are rejected client-side. */
  maxBytes?: number;
  className?: string;
}

/**
 * Drag-and-drop + click-to-pick image uploader designed to live next
 * to an avatar/logo preview. Used for both business logos and client
 * photos — the only difference is the `preview` slot and the upload
 * mutation passed in via `onUpload`.
 *
 * Why a single component:
 *   - Both surfaces want the same UX: hover-zone with file picker,
 *     drag-and-drop, an inline "uploading…" spinner, and a Remove
 *     button when an image already exists.
 *   - Centralising the file-size + mime checks here means the same
 *     guardrail runs on every upload site instead of every screen
 *     reinventing it.
 */
export function ImageUploader({
  preview,
  onUpload,
  onRemove,
  loading = false,
  hasImage = false,
  title = 'Photo',
  hint = 'PNG, JPG or WebP up to 5 MB.',
  accept = DEFAULT_ACCEPT,
  maxBytes = DEFAULT_MAX_BYTES,
  className,
}: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validate = useCallback(
    (file: File): string | null => {
      const allowed = accept.split(',').map((s) => s.trim());
      if (file.size > maxBytes) {
        const mb = (maxBytes / (1024 * 1024)).toFixed(0);
        return `That image is bigger than ${mb} MB. Try a smaller one.`;
      }
      if (allowed.length && !allowed.includes(file.type)) {
        return `Unsupported format (${file.type || 'unknown'}). Use PNG, JPG, or WebP.`;
      }
      return null;
    },
    [accept, maxBytes],
  );

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      const reason = validate(file);
      if (reason) {
        setError(reason);
        return;
      }
      try {
        await onUpload(file);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed.');
      }
    },
    [onUpload, validate],
  );

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          {title}
        </h3>
        {loading ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" /> Uploading…
          </span>
        ) : null}
      </div>

      <div
        onDragOver={(event) => {
          event.preventDefault();
          if (!loading) setDragActive(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setDragActive(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragActive(false);
          if (loading) return;
          const file = event.dataTransfer.files?.[0];
          if (file) void handleFile(file);
        }}
        className={cn(
          'group relative flex flex-col items-center gap-3 rounded-xl border border-dashed border-line bg-card p-5 text-center transition-colors sm:flex-row sm:text-left',
          dragActive && 'border-accent bg-accent/5',
          loading && 'cursor-wait opacity-80',
        )}
      >
        <div className="relative shrink-0">
          {preview}
          {!loading ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              aria-label="Change image"
              className="absolute -bottom-1 -right-1 inline-flex size-7 items-center justify-center rounded-full border border-line bg-background text-foreground shadow-sm transition-colors hover:bg-secondary"
            >
              <Camera className="size-3.5" />
            </button>
          ) : null}
        </div>
        <div className="flex flex-1 flex-col items-center gap-2 sm:items-start">
          <p className="text-sm text-muted-foreground">
            Drag an image here, or{' '}
            <button
              type="button"
              className="font-medium text-foreground underline-offset-2 hover:underline disabled:opacity-50"
              onClick={() => inputRef.current?.click()}
              disabled={loading}
            >
              browse to upload
            </button>
            .
          </p>
          <p className="text-xs text-muted-foreground">{hint}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={loading}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="size-4" />
              {hasImage ? 'Replace' : 'Upload'}
            </Button>
            {hasImage && onRemove ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={loading}
                onClick={() => {
                  setError(null);
                  void onRemove();
                }}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="size-4" /> Remove
              </Button>
            ) : null}
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void handleFile(file);
          }}
        />
      </div>

      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
